import { useEffect, useMemo, useState } from 'react';
import { Badge, Group, Loader, MultiSelect, Select, Stack, Table, Text, ThemeIcon } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import HttpClient from '../helpers/HttpClient';
import dayjs from 'dayjs';

interface PlayerMatchEventDTO {
  type: string;
  minute?: string;
}

interface PlayerMatchDTO {
  matchId: number;
  matchDate: string;
  season: string;
  matchType: string;
  competition?: string;
  homeTeamName: string;
  awayTeamName: string;
  homeGoals?: number;
  awayGoals?: number;
  isStartingSquad: boolean;
  isSubstituteSquad: boolean;
  isSubstitutedIn: boolean;
  substitutedInMinute?: string;
  playerEvents: PlayerMatchEventDTO[];
}

interface PlayerMatchesResponseDTO {
  matches: PlayerMatchDTO[];
}

const Checkmark = () => (
  <ThemeIcon color="green" variant="light" size="sm">
    <IconCheck size={12} />
  </ThemeIcon>
);

export const PersonMatchList = ({ personId }: { personId: number }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PlayerMatchesResponseDTO | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<string | null>(null);
  const [selectedBewerbe, setSelectedBewerbe] = useState<string[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const client = new HttpClient<PlayerMatchesResponseDTO>(`/person/${personId}/matches`);
        const result = await client.getSingle();
        setData(result);
      } catch (error) {
        console.error('Failed to fetch match data', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [personId]);

  const seasons = useMemo(() => {
    if (!data) return [];
    return [...new Set(data.matches.map(m => m.season))].sort().reverse();
  }, [data]);

  const bewerbe = useMemo(() => {
    if (!data) return [];
    return [...new Set(data.matches.map(m => m.competition || m.matchType))].sort();
  }, [data]);

  const filteredMatches = useMemo(() => {
    if (!data) return [];
    return data.matches
      .filter(m => !selectedSeason || m.season === selectedSeason)
      .filter(m => selectedBewerbe.length === 0 || selectedBewerbe.includes(m.competition || m.matchType));
  }, [data, selectedSeason, selectedBewerbe]);

  if (loading) return <Loader size="sm" />;

  return (
    <Stack gap="md">
      <Group>
        <Select
          placeholder="Alle Saisons"
          data={seasons}
          value={selectedSeason}
          onChange={setSelectedSeason}
          clearable
          w={160}
        />
        <MultiSelect
          placeholder="Alle Bewerbe"
          data={bewerbe}
          value={selectedBewerbe}
          onChange={setSelectedBewerbe}
          clearable
          w={240}
        />
      </Group>

      <Table striped highlightOnHover withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Datum</Table.Th>
            <Table.Th>Begegnung</Table.Th>
            <Table.Th>Bewerb</Table.Th>
            <Table.Th>Startelf</Table.Th>
            <Table.Th>Bank</Table.Th>
            <Table.Th>Eingewechselt</Table.Th>
            <Table.Th>Ereignisse</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {filteredMatches.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={7}>
                <Text c="dimmed" ta="center">Keine Spieleinsätze gefunden.</Text>
              </Table.Td>
            </Table.Tr>
          )}
          {filteredMatches.map(match => (
            <Table.Tr key={match.matchId}>
              <Table.Td>{dayjs(match.matchDate).format('DD.MM.YYYY')}</Table.Td>
              <Table.Td>
                {match.homeTeamName} – {match.awayTeamName}
                {match.homeGoals != null && match.awayGoals != null && (
                  <Text span c="dimmed" ml={6}>({match.homeGoals}:{match.awayGoals})</Text>
                )}
              </Table.Td>
              <Table.Td>
                <Text size="sm">{match.competition || match.matchType}</Text>
              </Table.Td>
              <Table.Td ta="center">{match.isStartingSquad && <Checkmark />}</Table.Td>
              <Table.Td ta="center">{match.isSubstituteSquad && !match.isSubstitutedIn && <Checkmark />}</Table.Td>
              <Table.Td ta="center">
                {match.isSubstitutedIn && (
                  <Text size="sm">{match.substitutedInMinute ? `${match.substitutedInMinute}'` : '✓'}</Text>
                )}
              </Table.Td>
              <Table.Td>
                <Group gap={4}>
                  {match.playerEvents
                    .filter(e => e.type !== 'Auswechslung')
                    .map((e, i) => (
                      <Badge key={i} size="sm" variant="light">
                        {e.minute ? `${e.minute}' ` : ''}{e.type}
                      </Badge>
                    ))}
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
        {data && (
          <Table.Tfoot>
            <Table.Tr>
              <Table.Td colSpan={3}><Text fw={600}>Gesamt</Text></Table.Td>
              <Table.Td ta="center"><Text fw={600}>{filteredMatches.filter(m => m.isStartingSquad).length}</Text></Table.Td>
              <Table.Td ta="center"><Text fw={600}>{filteredMatches.filter(m => m.isSubstituteSquad && !m.isSubstitutedIn).length}</Text></Table.Td>
              <Table.Td ta="center"><Text fw={600}>{filteredMatches.filter(m => m.isSubstitutedIn).length}</Text></Table.Td>
              <Table.Td></Table.Td>
            </Table.Tr>
          </Table.Tfoot>
        )}
      </Table>
    </Stack>
  );
};
